import React, { useEffect } from 'react';
import { useState } from 'react';
import { PieChart, Pie, Legend, Cell, ResponsiveContainer } from 'recharts';
import CategoryDropdownPosition from './CategoryDropdownPosition';

const PhonemeChart = ({phoneme, data, count, count_inside, count_start, count_end, data_inside, data_start, data_end, sample, sample_start, sample_inside, sample_end}) => {
    const [position, setPosition] = useState("All positions");
    const [outdata, setOutdata] = useState(data);
    const [positionCount, setPositionCount] = useState(count);
    const [outsample, setSample] = useState(sample);
    
    let positions = ["All positions"];
    if (data_start) positions.push('start');
    if (data_inside) positions.push('inside');
    if (data_end) positions.push('end');

    const COLORS = ['#10B981', '#F59E0B', '#EF4444'];
    const RADIAN = Math.PI / 180;
    
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        // Don't show labels for very small slices (less than 5%)
        if (percent < 0.05) return null;
        
        // Calculate position - closer to center to ensure full visibility
        const radius = innerRadius + (outerRadius - innerRadius) * 0.4; // 40% from inner radius
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text 
                x={x} 
                y={y} 
                fill="white" 
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fontWeight="bold"
                style={{
                    textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                    filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
                }}
                className="select-none"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    useEffect(() => {
        if (position === 'start') {
            setOutdata(data_start);
            setPositionCount(count_start);
            setSample(sample_start);
        } else if (position === "end") {
            setOutdata(data_end);
            setPositionCount(count_end);
            setSample(sample_end);
        } else if (position === "inside") {
            setOutdata(data_inside);
            setPositionCount(count_inside);
            setSample(sample_inside);
        } else {
            setOutdata(data);
            setPositionCount(count);
            setSample(sample);
        }
    }, [position, data, data_start, data_end, data_inside, count, count_start, count_end, count_inside, sample, sample_start, sample_end, sample_inside]);

    return (
        <div className='w-full h-full flex flex-col bg-white rounded-lg p-4 shadow-sm border border-gray-200 min-w-0'>
            {/* Header Section */}
            <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
                <div className='text-center sm:text-left'>
                    <h3 className='text-xl font-bold text-blue-700'>{phoneme}</h3>
                    <p className='text-sm text-gray-600 mt-1'>Total: {positionCount}</p>
                </div>
                
                {positions.length > 1 && (
                    <div className='flex-shrink-0 min-w-[140px]'>
                        <CategoryDropdownPosition 
                            selectHandler={e => setPosition(e.target.value)} 
                            elValue={position} 
                            positions={positions}
                        />
                    </div>
                )}
            </div>

            {/* Sample Text */}
            {outsample && (
                <div className='mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200'>
                    <div className='flex items-center gap-2'>
                        <span className='text-green-600 text-sm font-medium'>Sample:</span>
                        <span className='text-lg text-blue-800 font-semibold break-words'>{outsample}</span>
                    </div>
                </div>
            )}

            {/* Chart Container */}
            <div className='flex-1 flex items-center justify-center min-h-0'>
                <div className='w-full max-w-full h-full min-h-[200px]'>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Legend 
                                layout="horizontal" 
                                verticalAlign="bottom" 
                                align="center"
                                wrapperStyle={{ 
                                    paddingTop: '10px',
                                    fontSize: '12px'
                                }}
                            />
                            <Pie
                                data={outdata}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={renderCustomizedLabel}
                                outerRadius={80}
                                innerRadius={35} // Smaller inner radius for more label space
                                fill="#8884d8"
                                dataKey="value"
                                paddingAngle={0} // Remove padding for more slice space
                                startAngle={90} // Start from top for better visual
                                endAngle={-270} // Complete circle
                            >
                                {outdata.map((entry, index) => (
                                    <Cell 
                                        key={`cell-${index}`} 
                                        fill={COLORS[index % COLORS.length]} 
                                        stroke="#fff"
                                        strokeWidth={2}
                                    />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Accuracy Labels - Always show detailed breakdown */}
            <div className='mt-4 grid grid-cols-3 gap-2 text-xs border-t border-gray-200 pt-3'>
                {outdata.map((entry, index) => {
                    const percentage = positionCount > 0 ? (entry.value / positionCount * 100) : 0;
                    const showLabel = percentage >= 5; // Only show label in chart if >= 5%
                    
                    return (
                        <div key={index} className='flex flex-col items-center text-center p-1'>
                            <div className='flex items-center gap-1 mb-1'>
                                <div 
                                    className='w-3 h-3 rounded-full' 
                                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                ></div>
                                <span className='font-medium text-gray-700 text-[10px] leading-tight'>
                                    {entry.name}
                                </span>
                            </div>
                            <div className='flex flex-col'>
                                <span className='text-gray-600 font-bold text-sm'>
                                    {entry.value}
                                </span>
                                <span className={`text-[10px] ${!showLabel ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                                    {percentage.toFixed(0)}%
                                    {!showLabel && ' *'}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Small slices note */}
            {outdata.some(item => (item.value / positionCount * 100) < 5) && (
                <div className='mt-2 text-center'>
                    <span className='text-[10px] text-gray-500 italic'>
                        * Labels not shown for slices smaller than 5%
                    </span>
                </div>
            )}
        </div>
    );
}

export default PhonemeChart;